import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { confirmAppLeave } from '@/lib/appLeaveGuard';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { useCurrentUserMembershipTitle } from '@/hooks/useCurrentUserMembershipTitle';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { Avatar } from '@/components/ui/Avatar';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

export function UserAccountCard({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const { user, isPlatformAdmin } = useAuth();
  const { activeClinic } = useActiveClinic();
  const { data: profile } = useCurrentUserProfile();
  const { data: membershipTitle } = useCurrentUserMembershipTitle(activeClinic?.id);
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = profile?.name?.trim() || user?.email || '';
  const title = membershipTitle?.trim() ?? '';
  const onAccount = location.pathname.startsWith('/account');

  function go(to: string) {
    confirmAppLeave(() => {
      navigate(to);
      onNavigate?.();
    });
  }

  return (
    <MenuPopover
      align="start"
      title={displayName}
      className={collapsed ? 'bottom-full mb-s2 mt-0' : 'bottom-full mb-s2 mt-0 w-full min-w-0'}
      trigger={
        <button
          type="button"
          aria-label={t('nav.account_security')}
          title={displayName}
          className={cn(
            TYPE.control,
            collapsed
              ? 'flex h-10 w-10 items-center justify-center rounded-ctl'
              : 'flex w-full items-center gap-s3 rounded-ctl px-s3 py-s2 text-left',
            onAccount
              ? 'bg-ds-teal-tint font-semibold text-ds-teal'
              : 'font-medium text-ds-text hover:bg-ds-neutral-tint',
          )}
        >
          <Avatar name={displayName} size={28} />
          {!collapsed ? (
            <>
              <div className="min-w-0 flex-1">
                <div className={cn(TYPE.control, 'truncate font-semibold')}>{displayName}</div>
                {title ? (
                  <div className={cn(TYPE.caption, 'truncate text-ds-muted')} title={title}>
                    {title}
                  </div>
                ) : null}
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-ds-muted" aria-hidden />
            </>
          ) : null}
        </button>
      }
    >
      <MenuItem active={onAccount} onClick={() => go('/account/security')}>
        {t('nav.account_security')}
      </MenuItem>
      {isPlatformAdmin ? (
        <MenuItem active={location.pathname.startsWith('/admin')} onClick={() => go('/admin')}>
          {t('nav.admin')}
        </MenuItem>
      ) : null}
    </MenuPopover>
  );
}
